import { ProfileDropdownModal } from "@modules/Navbar/components/ProfileDropdownModal";
import { useAuthActions } from "@modules/auth/provider/AuthActionsProvider";
import { useLocale } from "@modules/shared/hooks/useLocale";
import { useAuthCheck } from "@modules/shared/hooks/useAuthCheck";
import { Link } from "@tanstack/react-router";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@ui/components/ui/dropdown-menu";
import { buttonVariants } from "@ui/components/variants/buttonVariants";
import { cn } from "@ui/lib/utils";
import { User } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@ui/components/ui/button";

type Props = {
  isMobileView?: boolean;
  closeNavigation?: () => void;
};

export const ProfileButton: React.FC<Props> = ({
  isMobileView,
  closeNavigation,
}) => {
  const { t } = useTranslation("Navbar");
  const locale = useLocale();
  const { isAuthenticated } = useAuthCheck();
  const { login, logout } = useAuthActions();
  const [isOpen, setIsOpen] = useState(false);

  const handleCloseNavigation = () => {
    closeNavigation?.();
    setIsOpen(false);
  };

  if (isMobileView) {
    return (
      <div className="group relative h-full px-4 transition-colors duration-150">
        <button
          type="button"
          className="flex h-full items-center gap-2"
          onClick={() => setIsOpen(previous => !previous)}
        >
          <User size={16} />
          <p>{t("myAccount")}</p>
        </button>

        <ProfileDropdownModal
          closeDropdown={() => setIsOpen(false)}
          showDropdown={isOpen}
          isAuthenticated={isAuthenticated}
          onLogin={login}
          onLogout={logout}
        />
      </div>
    );
  }

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          dark="ghost"
          type="button"
          size={"small"}
          className="flex gap-2"
          aria-label={t("myAccount")}
        >
          <User size={16} />
          <p className="text-base">{t("myAccount")}</p>
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        align="end"
        className="mt-2 w-55 rounded-xl border-border-5 bg-background-7 text-text-1"
      >
        {isAuthenticated ? (
          <>
            <DropdownMenuItem asChild>
              <Link
                onClick={handleCloseNavigation}
                to="/$locale/account/profile"
                params={{ locale }}
                className="cursor-pointer"
              >
                {t("profileDropdown.profile")}
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link
                onClick={handleCloseNavigation}
                to="/$locale/account/orders"
                params={{ locale }}
                className="cursor-pointer"
              >
                {t("profileDropdown.orders")}
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link
                onClick={handleCloseNavigation}
                to="/$locale/account/addresses"
                params={{ locale }}
                className="cursor-pointer"
              >
                {t("profileDropdown.addresses")}
              </Link>
            </DropdownMenuItem>
            <DropdownMenuSeparator className="bg-border-5" />
            <DropdownMenuItem
              onSelect={event => {
                event.preventDefault();
                void logout();
                setIsOpen(false);
              }}
              className="cursor-pointer text-error"
            >
              {t("profileDropdown.logout")}
            </DropdownMenuItem>
          </>
        ) : (
          <>
            <div className="p-2">
              <button
                type="button"
                onClick={() => {
                  void login();
                  setIsOpen(false);
                }}
                className={cn(
                  buttonVariants({ primary: "default", size: "small" }),
                  "w-full"
                )}
              >
                {t("profileDropdown.login")}
              </button>
            </div>
            <DropdownMenuSeparator className="bg-border-5" />
            <DropdownMenuItem asChild>
              <Link
                onClick={handleCloseNavigation}
                to="/$locale/info/return-policy"
                params={{ locale }}
                className="cursor-pointer"
              >
                {t("profileDropdown.returnPolicy")}
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link
                onClick={handleCloseNavigation}
                to="/$locale/info/payment-options"
                params={{ locale }}
                className="cursor-pointer"
              >
                {t("profileDropdown.paymentOptions")}
              </Link>
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
